/**
 * Recorte deliberado del router real: solo lo que el backend de este spike
 * sostiene hoy. Tracking, importacion, auditoria, reporte, admin y MFA se
 * retiran de las rutas -- no se dejan como paginas rotas. AppShell ya oculta
 * esos enlaces de nav cuando el permiso falta, asi que el usuario nunca ve
 * un vinculo muerto.
 *
 * Reconectados en pasadas anteriores (backend ya los sostenia desde los
 * Sprints 5 y 7, solo faltaba el frontend): tablero institucional, tablero
 * de unidad y el ciclo de revision -- este ultimo vive DENTRO de
 * ActivityDetail, no como paginas separadas (ver nota en AppShell.tsx).
 *
 * Reconectado: el asistente de 9 pasos completo (ActivityWizard.tsx), en
 * /actividades/nueva (crear) y /actividades/:id/editar (editar) -- antes
 * ambos eran vinculos muertos desde ActivitiesList.tsx y ActivityDetail.tsx.
 *
 * Reconectado en esta pasada: seguimiento del levantamiento (Tracking.tsx,
 * /seguimiento) -- modulo separado del RAT, backend nuevo en src/tracking.ts.
 *
 * Nuevo (no existia en el sistema original, ver src/catalog-admin.ts):
 * catalogos administrables (CatalogsAdmin.tsx, /catalogos) -- gateado con
 * el mismo permiso 'institutional.view' que el tablero institucional, no
 * uno granular nuevo (mismo colapso documentado en el README del backend).
 *
 * Reconectado en esta pasada (existia como backend puro, sin UI ni rutas
 * HTTP -- ver src/admin.ts): consola de usuarios (UsersAdmin.tsx,
 * /usuarios), mismo gateo que catalogos.
 *
 * Reconectado en esta pasada (existia solo /audit/verify, un veredicto sin
 * forma de ver los eventos -- ver src/audit.ts): Auditoria (Audit.tsx,
 * /bitacora), mismo gateo. El alcance de que se audita esta documentado
 * en la cabecera de src/audit.ts. Ruta deliberadamente NO "/auditoria":
 * choca por prefijo con el proxy de Vite para la API "/audit" (Vite hace
 * match de proxy por prefijo del pathname, no por ruta exacta), lo que
 * reenvia la navegacion de pagina completa al Worker en vez de servir la
 * SPA -- encontrado probando esta misma pantalla en local.
 *
 * Reconstruido para igualar el alcance real del proyecto original (rama
 * `main`, commit 5979987 -- ver src/import.ts): importacion CON STAGING Y
 * CURACION (Import.tsx, /actividades/importar) -- lee cualquier .xlsx/.csv
 * (SheetJS, todas las hojas), mapeo de columnas editable, resolucion de
 * unidad por nombre editable, vista previa incluir/excluir. Dos tipos:
 * "Actividades (a borrador)" y "Contactos de seguimiento". Gateada con
 * 'activity.create' a nivel de PAGINA (capa 1, UX -- mismo permiso que
 * "+ Nueva actividad"); el backend revalida por fila y por tipo --
 * "Contactos" en particular exige institucional (mismo colapso que el
 * resto de /seguimiento, ver tracking.ts), asi que un colaborador puede
 * ENTRAR a la pagina pero sus filas de tipo contacto salen 'forbidden'
 * al importar, igual que pasaria si el propio backend rechaza cualquier
 * otra escritura fuera de su unidad.
 *
 * Reconectado en esta pasada (backend completo desde el principio,
 * src/mfa.ts -- solo faltaba el frontend): MFA (MfaEnroll.tsx en
 * /mfa/inscribir, MfaChallenge.tsx en /mfa/verificar). Rutas HERMANAS de
 * /login, FUERA de RequireSession -- durante el segundo factor no hay
 * sesion real todavia, solo un "pending token" de 5 minutos (ver
 * AuthProvider.tsx). guards.tsx ya redirigia aqui desde antes (mfa ===
 * 'not_enrolled'/'needs_challenge'); solo faltaba que estas dos rutas
 * existieran. Apagado por defecto (`auth.require_mfa` en app_settings),
 * asi que hoy nunca se activa solo con este cambio.
 *
 * Nuevo (no existia en el sistema original): reportes de cumplimiento
 * exportables (Report.tsx, /reportes institucional y
 * /reportes/unidad/:unitId por unidad). Sin backend nuevo -- reusa
 * fetchInstitutionalDashboard/fetchUnitDashboard/fetchActivities y agrega
 * una vista pensada para `window.print()` (el CSS `@media print` ya vivia
 * en styles.css sin que ninguna pantalla lo usara). Autorizacion resuelta
 * DENTRO del componente, igual que institucional/unidad -- no via
 * RequirePermission (que solo acepta un permiso, no un OR de dos).
 */
import { Suspense, lazy } from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';
import { can, type MyAuthz } from '@rat/shared';
import { AppShell } from './components/AppShell';
import { RequireAccess, RequirePermission, RequireSession } from './auth/guards';
import { useAuth } from './auth/AuthProvider';
import { hasAnywhere, type AccessResponse } from './auth/access';
import { Login } from './pages/Login';
import { MfaEnroll } from './pages/MfaEnroll';
import { MfaChallenge } from './pages/MfaChallenge';
import { ActivitiesList } from './pages/ActivitiesList';
import { ActivityDetail } from './pages/ActivityDetail';
import { ActivityWizard } from './pages/ActivityWizard';
import { Import } from './pages/Import';
import { InstitutionalDashboard } from './pages/InstitutionalDashboard';
import { UnitDashboard } from './pages/UnitDashboard';
import { ReviewDashboard } from './pages/ReviewDashboard';
import { ReviewQueue } from './pages/ReviewQueue';
import { RolesAdmin } from './pages/RolesAdmin';
import { JefeDashboard } from './pages/JefeDashboard';
import { Report } from './pages/Report';
import { Tracking } from './pages/Tracking';
import { CatalogsAdmin } from './pages/CatalogsAdmin';
import { UsersAdmin } from './pages/UsersAdmin';
import { Audit } from './pages/Audit';
import { NotFound, NoUnit, Unauthorized } from './pages/Misc';

// Los tableros del auditor y del administrador traen los graficos (recharts, ~115 KB gzip) que el resto de los usuarios
// nunca abre: se cargan al entrar a esas rutas y no pesan en el bloque principal.
const AuditorDashboard = lazy(() => import('./pages/AuditorDashboard').then((m) => ({ default: m.AuditorDashboard })));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard').then((m) => ({ default: m.AdminDashboard })));
const Loading = <p className="muted">Cargando…</p>;

/**
 * "Inicio" por rol -- lo personal primero: quien tiene una unidad propia
 * (jefe/colaborador/consulta, sea o no ademas institucional/auditor) cae
 * ahi directo, no en un tablero agregado -- pedido explicito tras usar la
 * cuenta real: "debiese arrojarme inmediatamente a mi unidad". El tablero
 * institucional/reportes siguen alcanzables desde el nav (ahora reordenado
 * con "Mi unidad" primero, ver AppShell.tsx) para quien igual necesite la
 * vista agregada. Solo cae en /institucional quien NO tiene unidad propia
 * pero si administra o audita (institutional.view / unit.read.all, ver
 * auth/access.ts); un revisor sin unidad propia va a /actividades; sin nada de eso, /sin-unidad.
 */
function landingRouteFor(authz: MyAuthz, access: AccessResponse): string {
  const first = authz.units[0];
  if (first) return `/unidad/${first.unit_id}`;
  // Un Auditor (lee todo, no administra) aterriza en su tablero; el administrador sigue en el institucional.
  if (hasAnywhere(access, 'dashboard.auditor') && !hasAnywhere(access, 'dashboard.admin')) return '/auditor';
  if (can(authz, 'institutional.view') || can(authz, 'unit.read.all')) return '/institucional';
  // Un Revisor RAT no tiene unidad propia pero SI trabajo: aterriza en su tablero (cola + respuestas por confirmar).
  // Antes caia en /sin-unidad, un callejon sin salida.
  if (hasAnywhere(access, 'dashboard.revisor')) return '/revision';
  if (can(authz, 'activity.read.own_unit')) return '/actividades';
  return '/sin-unidad';
}

function Landing() {
  const { authz, access } = useAuth();
  return <Navigate to={landingRouteFor(authz, access)} replace />;
}

export const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  { path: '/mfa/inscribir', element: <MfaEnroll /> },
  { path: '/mfa/verificar', element: <MfaChallenge /> },
  {
    path: '/',
    element: (
      <RequireSession>
        <AppShell />
      </RequireSession>
    ),
    children: [
      { index: true, element: <Landing /> },
      { path: 'sin-unidad', element: <NoUnit /> },
      { path: 'actividades', element: <ActivitiesList /> },
      { path: 'actividades/nueva', element: <ActivityWizard /> },
      {
        path: 'actividades/importar',
        element: (
          <RequirePermission perm="activity.create">
            <Import />
          </RequirePermission>
        ),
      },
      { path: 'actividades/:id', element: <ActivityDetail /> },
      { path: 'actividades/:id/editar', element: <ActivityWizard /> },
      { path: 'institucional', element: <InstitutionalDashboard /> },
      { path: 'unidad/:unitId', element: <UnitDashboard /> },
      {
        path: 'auditor',
        element: (
          <RequireAccess perm="dashboard.auditor">
            <Suspense fallback={Loading}>
              <AuditorDashboard />
            </Suspense>
          </RequireAccess>
        ),
      },
      // /tablero-admin y no /admin: /admin es prefijo del proxy de la API en desarrollo.
      {
        path: 'tablero-admin',
        element: (
          <RequireAccess perm="dashboard.admin">
            <Suspense fallback={Loading}>
              <AdminDashboard />
            </Suspense>
          </RequireAccess>
        ),
      },
      {
        path: 'jefe',
        element: (
          <RequireAccess perm="dashboard.jefe">
            <JefeDashboard />
          </RequireAccess>
        ),
      },
      // Espacio del Revisor RAT (Fase 5). Ruta /revision, NO /review*: el proxy de Vite matchea por prefijo.
      {
        path: 'revision',
        element: (
          <RequireAccess perm="dashboard.revisor">
            <ReviewDashboard />
          </RequireAccess>
        ),
      },
      {
        path: 'revision/bandeja',
        element: (
          <RequireAccess perm="activity.review">
            <ReviewQueue />
          </RequireAccess>
        ),
      },
      { path: 'reportes', element: <Report /> },
      { path: 'reportes/unidad/:unitId', element: <Report /> },
      { path: 'seguimiento', element: <Tracking /> },
      {
        path: 'catalogos',
        element: (
          <RequirePermission perm="institutional.view">
            <CatalogsAdmin />
          </RequirePermission>
        ),
      },
      {
        path: 'usuarios',
        element: (
          <RequirePermission perm="institutional.view">
            <UsersAdmin />
          </RequirePermission>
        ),
      },
      {
        path: 'roles',
        element: (
          <RequireAccess perm="user.manage">
            <RolesAdmin />
          </RequireAccess>
        ),
      },
      {
        path: 'bitacora',
        element: (
          <RequirePermission perm="audit.read">
            <Audit />
          </RequirePermission>
        ),
      },
      { path: 'no-autorizado', element: <Unauthorized /> },
      { path: 'no-encontrado', element: <NotFound /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]);
